import React from 'react';
import { FormattedMessage } from 'react-intl';
import pickBy from 'lodash-es/pickBy';
import { createSelector } from 'reselect';
import { stringify, parse } from 'query-string';
import endsWith from 'lodash-es/endsWith';
import find from 'lodash-es/find';
import moment from 'moment';

// custom
import defaultAvatarImage from "assets/img/default-avatar.png";
import { fetch } from 'store/sagas/request';
import { 
    STORAGE_AUTH_TOKEN_KEY, 
    STORAGE_EXPIRES_AT_KEY, 
    STORAGE_AUTOLOGOUT_MIN_KEY,
    STORAGE_ROLE_TYPE,
    STORAGE_COMPANY_ID,
    ROLE_TYPE_APPLICATION_ADMIN,
    ROLE_TYPE_COMPANY_ADMIN
} from 'util/constants';


//************************************ 검색 관련 ************************************//
/**
 * 리스트 URL에서 필요한 쿼리스트링만 꺼내기 (페이지정보+검색조건)
 * @param {Object} props : location정보를 가진 오브젝트 
 */
const _validListQueryParams = ['page', 'pageSize', 'sortField', 'sortOrder'];
export const getListQueryString = createSelector(
    props => props.location.pathname,
    props => props.location.search,
    (path, search) => {
        const query = pickBy(
            parse(search),
            (value, key) => ( _validListQueryParams.indexOf(key) !== -1 || endsWith(key, 'Cond') )
        );
        return query;
    }
);


/**
 * default 검색 조건 구하기 
 * @param {Object} props : 검색조건 정보를 가진 오브젝트 
 */
export const getDefaultSearchConditions = (searchConditions) => {
    return searchConditions.filter(
        searchCondition =>
            searchCondition.props.options && 
            searchCondition.props.options.alwaysOn 
    );
}

/**
 * params에서 검색조건에 관련된 param만 가져오기 
 */
export const getSearchConditionsInParams = createSelector(
    state => state.main.params,
    (params) => {
        const searchConditionsInParams = pickBy(
            params,
            (value, key) => endsWith(key, 'Cond')
        );
        return searchConditionsInParams;
    }
);




//************************************ 파일 관련 ************************************//
/**
 * 파일 다운로드
 * @param {*} url 
 * @param {*} options 
 * @param {*} fileName 
 * @param {*} previewTagId  값이 있으면 미리보기로 제공  
 */
export function download(url, options, fileName, previewTagId) {
    const fetchOptions = {
        method: 'get',
        responseType: 'blob',
        onDownloadProgress: function (progressEvent) {
            const currentProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log('progress:',currentProgress);
        },
        ...options
    }

    return fetch(url, fetchOptions)
        .then((response) => {
            // 미리보기 기능 
            if( previewTagId ) {
                var image = document.getElementById(previewTagId);
                image.addEventListener("load", function (evt) {
                    URL.revokeObjectURL(evt.target.src);
                });
                image.src = window.URL.createObjectURL(new Blob([response.data]));
                image.parentElement.classList.toggle("hidden");
            // 다운로드 기능 
            } else {
                // IE
                if ( navigator.msSaveBlob ) {
                    navigator.msSaveBlob( new Blob([response.data]), fileName );
                // 기타 브라우저
                } else {
                    // Anchor tag 
                    const blobURL = window.URL.createObjectURL(new Blob([response.data]));
                    const downloadAnchorTag = document.createElement('a');
                    downloadAnchorTag.href = blobURL;
                    downloadAnchorTag.setAttribute('download', fileName);
                    document.body.appendChild(downloadAnchorTag);

                    // download
                    downloadAnchorTag.click();

                    // tag 제거
                    document.body.removeChild(downloadAnchorTag);
                    window.URL.revokeObjectURL(blobURL);
                }
            }
        });
}


/**
 * 파일사이즈에 어울리는 용량으로 표시 
 * @param {number} size : bytes단위의 파일사이즈
 */
export const getHumanReadableFileSize = (size) => {
    let selectedSize = 0;
    let selectedUnit = "bytes";
    const filesizeBase = 1024;

    if (size > 0) {
        const units = ['TB', 'GB', 'MB', 'KB', 'bytes'];

        for (var idx = 0; idx < units.length; idx++) {
            var unit = units[idx];
            var cutoff = Math.pow(filesizeBase, 4 - idx) / 10;

            if (size >= cutoff) {
                selectedSize = size / Math.pow(filesizeBase, 4 - idx);
                selectedUnit = unit;
                break;
            }
        }

        selectedSize = Math.round(10 * selectedSize) / 10; // Cutting of digits
    }

//    return "<strong>" + selectedSize + "</strong> " + selectedUnit;
    return selectedSize + selectedUnit;
}

/**
 * 링크를 통한 파일 다운로드
 * @param {*} link 
 */
export function linkFileDownload(link) {

    const downloadAnchorTag = document.createElement('a');
    downloadAnchorTag.href = link;
    document.body.appendChild(downloadAnchorTag);

    // download
    downloadAnchorTag.click();

    // tag 제거
    document.body.removeChild(downloadAnchorTag);
    window.URL.revokeObjectURL(link);
}

//************************************ 기타 ************************************//
/**
 * 사용자 Profile 기본 이미지 
 * @param {*} event 
 */
export const showDefaultAvataImg = (event) => {
    event.target.src = defaultAvatarImage;
}

export const getTopMenu = createSelector(
    props => props.location && props.location.pathname.toLowerCase(),
    (pathname) => {
        const pathInfos = pathname ? pathname.split("/") : [];
        return pathInfos.length > 1 ? pathInfos[1] : null;
    }
);

/**
 * 사용자에게 보여줄 로그아웃 메세지 키 
 */
export const getLogoutMsgKey = (errorCode, defaultLogoutMsgKey) => {
    const accessToken = localStorage.getItem(STORAGE_AUTH_TOKEN_KEY);
    let logoutMsgKey;
    console.log("accessToken:",accessToken);
    if( !accessToken ) {
        // 사용자가 로그아웃 버튼을 클릭한 경우 
        if( localStorage.getItem(STORAGE_EXPIRES_AT_KEY) ) {
            console.log("로그아웃 (버튼 클릭)");
            //return null;
            logoutMsgKey = 'logout.logout';//<FormattedMessage id="logout.logout"/>;//'로그아웃되었습니다.';
        // 로그인 정보가 없는 경우 
        } else {
            console.log("로그아웃 (로그인되어 있지 않음)")
        }
    // 토큰정보는 있지만 시간이 만료된 경우 
    } else if( !isCheckAuthenticated() ) {
        console.log("로그아웃 (자동로그아웃)");
        logoutMsgKey = 'logout.auto';//<FormattedMessage id="logout.auto"/>;
    // 기타
    } else {
        if( errorCode === 420 ) {
            console.log("로그아웃 (다른 디바이스에서 로그인)");
            logoutMsgKey = 'response.error.420';
        } else {
            console.log("로그아웃 (서버로그아웃)");
            logoutMsgKey = 'logout.server';
        }
    }
    //removeAllLoginInfo();

    return logoutMsgKey ? logoutMsgKey : defaultLogoutMsgKey;
}



//************************************ 기타 (webadmin용)  ************************************//
/**
 * 로그인 인증이 유효한지 체크 
 */
export const isCheckAuthenticated = () => {
    const accessToken = localStorage.getItem(STORAGE_AUTH_TOKEN_KEY);
    const expiresAt = localStorage.getItem(STORAGE_EXPIRES_AT_KEY);
    const roleType = localStorage.getItem(STORAGE_ROLE_TYPE);
    const isValidToken = accessToken && new Date().getTime() < expiresAt;
    // token이 유효한 경우 만료시간 재설정
    if( isValidToken && ( roleType === ROLE_TYPE_APPLICATION_ADMIN || roleType === ROLE_TYPE_COMPANY_ADMIN ) ) {
        const autoLogoutMinutes = localStorage.getItem(STORAGE_AUTOLOGOUT_MIN_KEY);
        const expiresAt = (autoLogoutMinutes * 60 * 1000) + new Date().getTime();
        localStorage.setItem(STORAGE_EXPIRES_AT_KEY, expiresAt);
    }
    console.log("isCheckAuthenticated:",isValidToken);
    
    return isValidToken;
}

// 통합관리자 여부
export function isApplicationAdmin() {
    return localStorage.getItem(STORAGE_ROLE_TYPE) === ROLE_TYPE_APPLICATION_ADMIN;
}

// 현재 선택된 회사 정보 (state)
export const getSelectedCompanyInfo = createSelector(
    state => state.header.companies,
    (companies) => {
        const selectedCompany = companies ? find(companies, company => company.selected === true) : undefined;
        return selectedCompany ? selectedCompany : { id: 0 }; // 렌더링 시 id값으로 비교하기 위해서 정보없을 때 id값만 0으로 리턴 (렌더링 횟수를 줄이기 위해서)
    }
);

// 현재 선택된 회사의 id값 (localStorage)
export function getCompanyId() {
    return Number(localStorage.getItem(STORAGE_COMPANY_ID));
}

// 회사정보 변경 (localStorage)
export const setCompanyId = (companyId) => {
    localStorage.setItem(STORAGE_COMPANY_ID, companyId);
}


/**
 * 현재시간 구하기
 * @param {String} format : 시간 포맷
 */
export const getCurrentTime = (format) => {
    return moment().format(format);
}

/**
 * UTC 시간으로 변경
 * @param {Date} format : 시간 포맷
 */
export const convertUtcDate = date => (moment(date).utc().format());

export const makeSubTitleMessage = createSelector(
    (state, menuName) => Object.keys(getSearchConditionsInParams(state)).length > 0,
    (state, menuName) => state.main.totalCount,
    (state, menuName) => menuName,
    ( isSearched, totalCount, menuName ) => 
        <FormattedMessage 
            id={ isSearched  ? `${menuName}.search.count` : `${menuName}.total.count` }
            values={{ totalCount: totalCount }}
        />
);

export const makeRedirectUrl = createSelector(
    (state, linkUrl) => linkUrl,
    (state, linkUrl) => state.main.params,
    (linkUrl, params) => ({
        pathname: linkUrl,
        search: `?${stringify({
            ...params
        })}`
    })
);
